import type { PropsWithChildren } from 'react'
import { useEffect } from 'react'
import { publicConfig } from '@/config/publicConfig'
import { plexWebSocketClient } from '@/services/api/plex/PlexWebSocketClient'
import { getPlexUrl, withPlexQueryAuth } from '@/services/api/plex/transport'
import { useApiKey } from '@/utils/apiKeyAuth'
import { useFirebaseAuth } from '@/utils/firebaseAuthProvider'

export function PlexWebSocketProvider({ children }: PropsWithChildren) {
  const user = useFirebaseAuth()
  const apiKey = useApiKey()

  useEffect(() => {
    const url = getPlexUrl(publicConfig.api.baseUrl)

    // Match HTTP authentication precedence when both session sources are present.
    if (apiKey) {
      plexWebSocketClient.configure({
        key: `api-key:${apiKey}`,
        buildUrl: async () =>
          withPlexQueryAuth(url, 'x-api-key', apiKey, publicConfig.firebase.tenantId),
      })
      return
    }

    if (user) {
      plexWebSocketClient.configure({
        key: `firebase:${user.uid}`,
        buildUrl: async () => {
          // Resolve the token for each connection attempt so reconnects do not reuse an expired token.
          const token = await user.getIdToken()
          return withPlexQueryAuth(url, 'token', token, publicConfig.firebase.tenantId)
        },
      })
      return
    }

    plexWebSocketClient.configure({
      key: 'public',
      buildUrl: async () => url,
    })
  }, [apiKey, user])

  // Reconfigure on auth changes without discarding subscriptions; shut down on provider teardown.
  useEffect(() => () => plexWebSocketClient.shutdown(), [])

  return children
}
